import { conditionEmoji, thunderSignificant } from './weatherData'

// No Unicode emoji reads as "fog" at a glance — 🌫️ renders as a blurry gray
// smudge, 🌁 is a bridge-in-a-landscape scene, 🌥️/☁️ collide with other
// conditions. A small hand-drawn glyph (cloud silhouette + wavy mist lines
// beneath it, the same visual language Apple Weather's own icon uses) reads
// clearly at this size where no emoji does.
// `color` is the solid cloud fill; the mist lines derive their fade from it
// via opacity rather than a hardcoded rgba, so the same glyph works on the
// rail's always-dark tiles (white) and CurrentConditionsHero's light/dark
// scheme-following card (which needs a dark fill in light mode).
function FogIcon({ size = 22, color = '#fff' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ flexShrink: 0, verticalAlign: 'middle' }}
      aria-hidden="true"
    >
      <circle cx="9" cy="10.5" r="4" fill={color} />
      <circle cx="14.5" cy="8" r="5" fill={color} />
      <circle cx="18.5" cy="11" r="3.2" fill={color} />
      <rect x="6" y="10" width="15" height="5.5" rx="2.75" fill={color} />
      <path
        d="M3 18 Q5.5 16.6 8 18 T13 18 T18 18 T22 18"
        stroke={color}
        strokeOpacity={0.6}
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M5 21.5 Q7.5 20.1 10 21.5 T15 21.5 T20 21.5"
        stroke={color}
        strokeOpacity={0.45}
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  )
}

// Drop-in replacement for conditionEmoji() at every call site — a plain
// string for every condition except fog, so it works equally well inside a
// <Text> child or passed as a plain `emoji` prop. `size` should match the
// font-size the emoji would otherwise have rendered at (callers already know
// this — GLYPH_FZ in most of the rail, WeekSection's smaller header size).
// `color` only matters for fog; every other call site is always on the
// rail's dark tiles, so the default white is right everywhere except
// CurrentConditionsHero's light-mode card.
export function conditionGlyph(code, night = false, size = 22, color = '#fff') {
  if (code === 'fog') return <FogIcon size={size} color={color} />
  return conditionEmoji(code, night)
}

// The single condition glyph standing for a whole day — the Week card's
// "MARINE WEATHER" band header. Applies the same thunder override the hourly
// rows and the hero use (`conditions`/`thunder` are independent upstream
// signals, so a day can read "clear" while carrying real storm risk), which
// is exactly why this is a helper and not a bare conditionGlyph() call at the
// header: the day's one icon must not contradict the day it summarizes.
// Always the day variant — a whole day is never "night".
export function dayConditionGlyph(day, size = 17) {
  if (!day) return null
  const code = thunderSignificant(day.thunder_worst) ? 'thunderstorms' : day.conditions_dominant
  return conditionGlyph(code, false, size)
}
